import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "../../../ui/Button";
import { ChipRow } from "../../../ui/ChipRow";
import { displayToast } from "../../../app/AppToast";
import { apiClient } from "../../../../services/api";
import {
  closeAnnotation,
  popAnnotationView,
  revalidateSubjectAnnotations,
  type AnnotationTarget,
} from "../../../../domains/gpt/stores/annotationSheetStore";

type SkipReason = "out_of_my_specialty" | "ambiguous_question";

/**
 * Passing on a turn, with a reason.
 *
 * Its own view rather than a button on the form, because a skip is an OUTCOME and
 * not an escape: an expert who cannot judge a turn has to be able to say so — the
 * alternative is that they guess, and the ground truth degrades with nothing to
 * signal it. The reason is a closed set, so the corpus can tell "outside my
 * specialty" from "the question was ambiguous", which are different problems.
 */
export function SkipTurnView({
  target,
}: {
  target: AnnotationTarget;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { watch, setValue, handleSubmit, formState } = useForm<{
    reason: SkipReason | null;
  }>({ defaultValues: { reason: null } });
  const reason = watch("reason");

  return (
    <form
      className="flex min-h-full flex-col"
      onSubmit={handleSubmit(async () => {
        if (reason === null) return;
        const subject = target.subject;
        const verdict = { kind: "skipped", scaleVersion: 1, reason } as const;
        try {
          if (subject.engine === "legacy")
            await apiClient.post(
              "/webapp/private/conversations/:id/annotations",
              {
                param: { id: subject.conversationId },
                json: { turnIndex: subject.turnIndex, verdict },
              }
            );
          else
            await apiClient.post(
              "/assistant/conversations/:conversationId/annotations",
              {
                param: { conversationId: subject.conversationId },
                json: { runId: subject.runId, verdict },
              }
            );
          await revalidateSubjectAnnotations(subject);
          // A skip ends the flow: there is nothing to compare a judgement to.
          closeAnnotation();
        } catch {
          displayToast(t("Something went wrong. Please try again."), "error");
        }
      })}
    >
      <div className="flex flex-1 flex-col gap-4 px-5 py-6 ">
        <p className="font-dm-sans text-ui-body font-normal text-ds-slate-600">
          {t("Why are you not judging this answer?")}
        </p>
        <ChipRow
          value={reason}
          onChange={(value) => {
            setValue("reason", value);
          }}
          label={t("Why are you not judging this answer?")}
          items={[
            {
              value: "out_of_my_specialty",
              label: t("Outside my specialty"),
            },
            { value: "ambiguous_question", label: t("Ambiguous question") },
          ]}
        />
      </div>

      <div className="sticky bottom-0 z-10 flex shrink-0 gap-3 border-t border-ds-slate-200 bg-white px-5 py-4">
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={popAnnotationView}
        >
          {t("Back")}
        </Button>
        <Button
          type="submit"
          variant="primary"
          className="flex-1"
          disabled={reason === null || formState.isSubmitting}
        >
          {t("Skip this answer")}
        </Button>
      </div>
    </form>
  );
}
